import React, { useState, useMemo } from "react";
import { Box, Typography, Tabs, Tab } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

import CollapseCard from "components/Collapse";
import TwoColumnContainer from "components/Container/TwoColumnContainer";
import { wirelessModeOptions } from "utils/channel-options/const";

const fields = [
  { key: "channel", label: "Channel" },
  { key: "chanWidth", label: "Channel Width" },
  { key: "wirelessMode", label: "Mode" },
  { key: "txPower", label: "Transmit Power" },
  { key: "clientsCount", label: "Clients" },
  { key: "rssiThreshold", label: "RSSI Threshold" },
];

const RadioPanel = ({ wirelessRadioData, clientRowData, apRadioData }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const clientsByBand = useMemo(() => {
    return {
      2: clientRowData.filter((client) => client.band === "2.4G"),
      5: clientRowData.filter((client) => client.band.includes("5G")),
      6: clientRowData.filter((client) => client.band === "6G"),
    };
  }, [clientRowData]);

  const currentData = useMemo(() => {
    const tmpRadioData = wirelessRadioData[tabValue];
    const tmpApRadioData = apRadioData?.[tabValue];

    if (!tmpRadioData) return {};

    const clients =
      tmpRadioData?.band && clientsByBand?.[tmpRadioData.band]
        ? clientsByBand[tmpRadioData.band]
        : [];

    return {
      ...tmpRadioData,
      ...tmpApRadioData,

      // apRadioData.channel 沒值時，才用 wirelessRadioData.channel
      channel: tmpApRadioData?.channel ?? tmpRadioData?.channel,

      clientsCount: clients.length,
      clients,
    };
  }, [tabValue, wirelessRadioData, apRadioData, clientsByBand]);

  const supportedBands = useMemo(
    () => wirelessRadioData.map((data) => data.band),
    [wirelessRadioData],
  );

  const StyledTypography = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.customFontSize[2],
    color: theme.palette.primary.black,
    fontWeight: theme.typography.customFontWeight[1],
  }));

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const currentBandLabel = useMemo(() => {
    if (!currentData?.band) return null;

    if (currentData.band === "2") return "2.4GHz";
    if (currentData.band === "5") return "5GHz";
    if (currentData.band === "6") return "6GHz";

    return null;
  }, [currentData]);

  const wirelessModeLabel = useMemo(() => {
    if (!currentBandLabel || !currentData?.wirelessMode) return "-";

    const options = wirelessModeOptions[currentBandLabel] || [];
    const matched = options.find(
      (opt) => opt.value === currentData.wirelessMode,
    );

    return matched?.label || currentData.wirelessMode || "-";
  }, [currentBandLabel, currentData]);

  return (
    <CollapseCard title="Radio">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            border: `1px solid ${theme.palette.primary.greyThread}`,
            borderRadius: "100px",
            backgroundColor: theme.palette.primary.white,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              minHeight: 38,
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 38,
                borderRadius: "100px",
                color: theme.palette.primary.black,
                transition: "all 0.2s ease",

                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.subBlue,
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            {supportedBands.includes("2") && <Tab label="2.4GHz" />}
            {supportedBands.includes("5") && <Tab label="5GHz" />}
            {supportedBands.includes("6") && <Tab label="6GHz" />}
          </Tabs>
        </Box>

        {fields.map(({ key, label }) => (
          <TwoColumnContainer key={key} label={label}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                alignItems: "center",
              }}
            >
              <StyledTypography>
                {key === "wirelessMode"
                  ? wirelessModeLabel
                  : key === "rssiThreshold"
                    ? currentData[key] != null
                      ? `${currentData[key]}%`
                      : "-"
                    : (currentData[key] ?? "-")}
              </StyledTypography>
            </Box>
          </TwoColumnContainer>
        ))}
      </Box>
    </CollapseCard>
  );
};

export default RadioPanel;
